import React, { useState, useRef } from "react";
import { useForm } from "react-hook-form";

import { stepperFormValidationMapper, validateIdentification, trb, i18 } from "@utilities";
import { Notification, NOTIFICATION_TYPES } from "@components";
import { getStep5Validations } from "../../Validations/step5";
import useFormStepControl from "@hooks/useFormStepControl";
import { ActionButtons } from "../../Components";
import { ClientBeneficiaries } from "./groups/ClientBeneficiaries";
import { TradeSecure } from "./groups/TradeSecure";

const getBenefSharesArray = beneficiaries => {
  const shares = [];
  beneficiaries.forEach(entity => {
    shares.push(Number(entity.data.clientBenefShare));
  });

  return shares;
};

const customBeneficiaryShareValidation = ({ notAbove75percent, allShares }) => {
  if (notAbove75percent || notAbove75percent === "1" || notAbove75percent === 1) return {};

  let totalShares = 0;
  const errors = [];
  for (let i = 0; i < allShares.length; i++) {
    if (allShares[i] >= 75) return {};
    totalShares += allShares[i];
    errors.push({
      data: {
        clientBenefShare: {
          message: `${trb(i18.Validations.ClientBenefShareTooSmall)}: 75`,
          type: "sharesToSmall"
        }
      }
    });
  }
  if (totalShares >= 75) return {};
  return {
    clientBeneficiaries: errors
  };
};

const getCombinedErrors = (stepValidationErrors, shareErrors, totalBeneficiaries) => {
  const combinedErros = {};

  for (let i = 0; i < totalBeneficiaries; i++) {
    const beneficiaryErrors = {};
    if (stepValidationErrors?.clientBeneficiaries?.[i]) {
      beneficiaryErrors.data = { ...stepValidationErrors.clientBeneficiaries[i].data };
    }
    if (shareErrors?.clientBeneficiaries?.[i]) {
      beneficiaryErrors.data = {
        ...beneficiaryErrors.data,
        ...shareErrors.clientBeneficiaries[i].data
      };
    }
    if (Object.keys(beneficiaryErrors).length) {
      if (!combinedErros.clientBeneficiaries) combinedErros.clientBeneficiaries = [];
      combinedErros.clientBeneficiaries[i] = {};
      Object.assign(combinedErros.clientBeneficiaries[i], beneficiaryErrors);
    }
  }
  return combinedErros;
};

export const FormStep5 = React.memo(() => {
  const {
    formDraft,
    submitForm,
    isSent,
    handleActionButtonClick,
    submitType,
    validateForm
  } = useFormStepControl();

  const [totalbeneficiariesAdded, setTotalBeneficiariesAdded] = useState(
    formDraft.personData?.clientBeneficiaries?.length
  );
  const [isValidatingId, setIsValidatingId] = useState(false);
  const sawIdErrorOnce = useRef(false);

  const { handleSubmit, control, setValue, unregister, setError } = useForm({
    resolver: (values, context) =>
      validateForm(values, context, () => {
        const { errors } = stepperFormValidationMapper(
          values,
          getStep5Validations({
            totalBeneficiaries: totalbeneficiariesAdded,
            clientBeneficiariesValues: values.clientBeneficiaries
          })
        );
        const allShares = getBenefSharesArray(values.clientBeneficiaries);
        const sharesError = customBeneficiaryShareValidation({
          notAbove75percent: values.clientBeneficiaries[0].data.uboretail,
          allShares
        });

        const combinedErros = getCombinedErrors(
          errors,
          sharesError,
          values.clientBeneficiaries.length
        );

        const entity = { values, errors: combinedErros };
        return entity;
      }),
    mode: "onSubmit",
    context: {
      submitType
    },
    shouldFocusError: true,
    defaultValues: formDraft.personData
  });

  const onSubmit = async data => {
    if (!sawIdErrorOnce.current && submitType === "next") {
      try {
        setIsValidatingId(true);
        for (let i = 0; i < totalbeneficiariesAdded; i++) {
          const idError = await validateIdentification({
            countryValidation: "LT", // ask about what country to list
            identificationCode: data.clientBeneficiaries[i].data.clientBenefId,
            countryValidationType: "PersonalIdentificationNumber"
          });
          if (idError) {
            setError(`clientBeneficiaries.${i}.data.clientBenefId`, idError, { shouldFocus: true });
            sawIdErrorOnce.current = true;
            return;
          }
        }
      } catch (e) {
        Notification({
          type: NOTIFICATION_TYPES.ERROR,
          message: trb(i18.NotificationMessages.SomethingWentWrong)
        });
      } finally {
        setIsValidatingId(false);
      }
    }

    submitForm(data, submitType);
  };

  const resetIsinCode = e => {
    if (e.target.value === 1) return;
    setValue("clientBeneficiaries.0.data.isinCode", "");
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <TradeSecure control={control} resetIsinCode={resetIsinCode} />
        <ClientBeneficiaries
          control={control}
          totalbeneficiariesAdded={totalbeneficiariesAdded}
          setTotalBeneficiariesAdded={setTotalBeneficiariesAdded}
          unregister={unregister}
        />
        <ActionButtons
          isLoading={isSent || isValidatingId}
          handlePrevStep={handleActionButtonClick}
          handleNextStep={handleActionButtonClick}
        />
      </form>
    </>
  );
});
